import { cloneDeep, isEmpty } from 'lodash';
import { TreeNode } from 'primeng/api';
import { Item } from './item';
import { Legend } from './legend';
import { HeaderElement, TableHeader } from './table-header';
import { Column } from './column';
import { Row } from './row';
import { ColumnType } from './column-type';
import { ResultType } from './result-type';
import { Info } from './info';

export class Result {
    public resultType: ResultType = ResultType.FlatTable; // default type is flat table
    public legend: Legend | null = null;
    public info: Info | null = null;
    public columns: Column[] = [];
    public rows: Row[] = [];
    public headerOrder: string[] = [];

    private measureColumnIds: string[] = [];
    public treeNodeCount = 0;

    static resultFactory( result: Result ): Result {
        let newResult = new Result();
        newResult.resultType = result.resultType;
        newResult.legend = result.legend;
        newResult.info = result.info;
        newResult.columns = result.columns;
        newResult.rows = result.rows;
        newResult.headerOrder = result.headerOrder;
        return newResult;
    }

    // ###### HEADER ######
    generateHierarchyHeader( sortedGroupableColums?: Column[] ): TableHeader {
        // HeaderHierarchy ist der Teil des Headers, bei dem die Kennzahlen mit den Spaltendimension kombiniert werden
        let headerHierarchy: Column[] = this.getHeaderHierarchy( this.headerOrder );
        let maxDepth: number = headerHierarchy[0].verticalHeaderElement?.length ?? -1;
        let header: TableHeader = new TableHeader( maxDepth );
        let attributeColumns: Column[] = sortedGroupableColums ? sortedGroupableColums : this.getAttributeColumns();
        attributeColumns.forEach( col => {
            let element: HeaderElement = new HeaderElement( col.header, col, maxDepth, 1 );
            element.sortIcon = true;
            header.headerRows[0].headerElements.push( element );
        } );
        this.addElementsToHeaderRow( header, headerHierarchy, 0, maxDepth );
        return header;
    }

    getHeaderHierarchy( columnOrder: string[] ): Column[] {
        let measureColumns = this.getMeasureColumns();
        if ( !columnOrder || columnOrder.length === 0 ) {
            columnOrder = this.getMeasureColumnHeaderKeys();
        }
        measureColumns.forEach( col => {
            col.verticalHeaderElement = col.header.split( ' || ' ).map( headerItem => {
                let headerItemKeyValue = headerItem.split( '|' );
                return new Item( headerItemKeyValue[0], headerItemKeyValue[1] );
            } );
        } );
        let headerHierarchy = this.groupColumnsByHeader( measureColumns, columnOrder, 0 );
        return headerHierarchy;
    }

    // column[0].header: "Bewerber: Kontinent|AFR || Bewerber: Wohnort (Landkreis)|Aachen, Stadt / Region || Kennzahl| Anträge (abgegeben)"
    // => ["Bewerber: Kontinent"]["Bewerber: Wohnort"]["Kennzahl"]
    // Überarbeitung wäre von Vorteil! Die Kennzahlen sollen standardmäßig in der oberen Headerzeile stehen, deshalb der Tausch.
    // Eigentlich sollte der header seperat gespeichert werden, zum Beispiel in eine "path-"Variable
    getMeasureColumnHeaderKeys(): string[] {
        let elements: string[] = [];
        if ( this.columns.length === 0 ) {
            return elements;
        }
        let measureColumns: Column[] = this.getMeasureColumns();
        let splitHeader: string[] = measureColumns[0].header.split( ' || ' );
        let measuresHeader: string[] = splitHeader.filter( s => s.startsWith( 'Kennzahl' ) );
        let others: string[] = splitHeader.filter( s => !s.startsWith( 'Kennzahl' ) );
        let orderedHeader: string[] = [...measuresHeader, ...others];
        orderedHeader.forEach( headerLevel => {
            let levelLabel = headerLevel.split( '|' )[0];
            elements.push( levelLabel );
        } );
        return elements;
    }

    private groupColumnsByHeader( columns: Column[], columnOrder: string[], depth: number ): Column[] {
        let headerHierarchy: Column[] = [];
        let currentColumn: string = columnOrder[0];
        if ( columns.length > 0 && currentColumn ) {
            headerHierarchy = this.buildColumnHierarchy( columns, currentColumn, depth );
            headerHierarchy.forEach( node => {
                let remainingOrder: string[] = columnOrder.filter( col => col !== columnOrder[0] );
                node.children = this.groupColumnsByHeader( node.children ?? [], remainingOrder, depth + 1 );
            } );
        }
        return headerHierarchy;
    }

    private buildColumnHierarchy( columns: Column[], currentColumn: string, depth: number ): Column[] {
        let headerHierarchy: Column[] = [];
        let categoryIndexMap = new Map<string, number>();
        columns.forEach( column => {
            let cellData: string | undefined = column.verticalHeaderElement?.find( item => item.label === currentColumn )?.value;
            if ( cellData === undefined ) {
                return;
            }
            if ( !categoryIndexMap.has( cellData ) ) {
                let newParent: Column = this.createNewParentColumn( column, cellData );
                let counter: number = headerHierarchy.push( newParent ) - 1;
                categoryIndexMap.set( cellData, counter );
            }
            if ( depth < ( column?.verticalHeaderElement?.length ?? -1 ) ) {
                let index: number | undefined = categoryIndexMap.get( cellData );
                headerHierarchy[index as any]?.children?.push( column );
            }
        } );
        return headerHierarchy;
    }

    private createNewParentColumn( column: Column, cellData: string ): Column {
        let newParent: Column = cloneDeep( column );
        newParent.children = [];
        newParent.header = cellData;
        return newParent;
    }

    private addElementsToHeaderRow( header: TableHeader, headerHierarchy: Column[], depth: number, maxdepth: number ) {
        headerHierarchy.forEach( node => {
            let colSpan: number = this.getNumberOfDeepestChildren( [node] );
            let element: HeaderElement = new HeaderElement( node.header, node, 1, colSpan );
            element.sortIcon = depth === maxdepth - 1;
            header.headerRows[depth].headerElements.push( element );
            if ( depth < maxdepth - 1 ) {
                this.addElementsToHeaderRow( header, node.children ?? [], depth + 1, maxdepth );
            }
        } );

    }

    private getNumberOfDeepestChildren( tree: Column[] ): number {
        if ( isEmpty( tree[0].children ) ) {
            return tree.length;
        }
        let count: number = 0;
        tree.forEach( node => {
            count = count + this.getNumberOfDeepestChildren( node.children ?? [] );
        } );
        return count;
    }

    // ###### Aufbauen der Baumstruktur aus den Zeilen ######
    buildHierarchicalDataStructure(): TreeNode[] {
        this.measureColumnIds = this.getMeasureColumnFields();
        this.treeNodeCount = 0;
        // BI-Analysen sind im Backend voraggregiert  - der Projektfinanzbericht auf Basis der SuperX-Strukturen hat das nicht
        let rows: Row[] = [];
        if ( !this.isBiAnalyse() ) {
            rows = this.enrichRowsWithAggregations( cloneDeep( this.rows ) );
        } else {
            rows = cloneDeep( this.rows );
        }
        let columnsToGroup = this.columns.filter( c => c.type !== ColumnType.SortOrderColumn );
        return this.buildTreeNodeStructure( rows, columnsToGroup );
    }

    buildTreeNodeStructure( rows: Row[], columns: Column[] ): TreeNode[] {
        let columnIds: string[] = columns.map( c => c.field );
        let hierarchy: TreeNode[] = [];
        let depth: number = 0;
        let rowsAtAggregation: Row[] = rows.filter( r => r.aggregated === depth );

        for ( let row of rowsAtAggregation ) {
            let newRow: TreeNode = { data: cloneDeep( row.cells ), children: [] };
            hierarchy.push( newRow );
            this.treeNodeCount++;
            this.addChildren( newRow, rows, depth + 1, columnIds );
        }
        return hierarchy;
    }

    addChildren( parentRow: TreeNode<any>, rows: Row[], depth: number, columns: string[] ) {
        let groupingColumn: string = columns[0];
        let descendants: Row[] = rows.filter( r => r.cells[groupingColumn] === parentRow.data[groupingColumn] );
        let children: Row[] = descendants.filter( r => r.aggregated === depth );
        let remainingColumns: string[] = columns.slice( 1 );

        for ( let child of children ) {
            // compact table if only one child with same values
            if ( children.length === 1 && this.childAndParentMeasuresAreEqual( parentRow, child ) ) {
                parentRow.data = child.cells;
                this.addChildren( parentRow, descendants, depth + 1, remainingColumns );
            } else {
                let newRow: TreeNode = { data: child.cells, children: [] };
                parentRow.children?.push( newRow );
                this.treeNodeCount++;
                this.addChildren( newRow, descendants, depth + 1, remainingColumns );
                this.clearLabels( newRow, remainingColumns );
            }
        }
    }

    childAndParentMeasuresAreEqual( parentRow: TreeNode<any>, child: Row ) {
        for ( let column of this.measureColumnIds ) {
            if ( parentRow.data[column] !== child.cells[column] ) {
                return false;
            }
        }
        return true;
    }

    // only optical reasons
    clearLabels( row: TreeNode<any>, columnsToKeep: string[] ) {
        for ( let column of Object.keys( row.data ) ) {
            if ( !columnsToKeep.includes( column ) ) {
                row.data[column] = '';
            }
        }
    }

    // für SuperX-(Projektfinanz-)bericht...
    private enrichRowsWithAggregations( rows: Row[] ): Row[] {
        let columnOrder: string[] = this.getAttributeColumnFields();
        let aggregationRows: Row[] = this.createAggregations( rows, columnOrder, 0 );
        return aggregationRows;
    }

    private createAggregations( rows: Row[], columnOrder: string[], depth: number ): Row[] {
        let currentColumn: string = columnOrder[0];
        let groupedValues: Set<string> = new Set<string>();
        let rowsWithAggregations: Row[] = [];

        for ( let row of rows ) {
            let groupableValue: string = row.cells[currentColumn];
            if ( !groupedValues.has( groupableValue ) ) {
                groupedValues.add( groupableValue );
                let descendants: Row[] = rows.filter( r => r.cells[currentColumn] === groupableValue );
                if ( columnOrder.length > 1 ) {
                    let aggregatedDescendants: Row[] = this.createAggregations( descendants, columnOrder.slice( 1 ), depth + 1 );
                    let children: Row[] = aggregatedDescendants.filter( r => r.aggregated === depth + 1 );
                    let measures: { [k: string]: number } = this.calculateAggregatedMeasures( children );
                    let parentRow: Row = this.createParent( row, measures, columnOrder, depth );
                    rowsWithAggregations.push( parentRow );
                    rowsWithAggregations.push( ...aggregatedDescendants );
                } else {
                    rows = rows.map( r => ( { ...r, aggregated: depth } ) );
                    return rows;
                }
            }
        }
        return rowsWithAggregations;
    }

    private createParent( row: Row, measures: { [k: string]: number; }, columnOrder: string[], depth: number ): Row {
        let parentCells: any = cloneDeep( row.cells );
        Object.assign( parentCells, measures );
        for ( let col of columnOrder.slice( 1 ) ) {
            parentCells[col] = '';
        }
        return new Row( parentCells, undefined, depth );
    }

    private calculateAggregatedMeasures( children: Row[] ): { [k: string]: number } {
        let measures: Map<string, number> = new Map<string, number>();
        for ( let child of children ) {
            for ( let measure of this.measureColumnIds ) {
                let current: number = measures.get( measure ) ?? 0;
                measures.set( measure, current + child.cells[measure] );
            }
        }
        return Object.fromEntries( measures );
    }

    getVisibleColumns(): Column[] {
        return this.columns.filter( col => col.hidden === undefined || col.hidden === false );
    }

    /** Liefert die Spalten mit Kennzahlen */
    getMeasureColumns(): Column[] {
        return this.getVisibleColumns()
            .filter( col => !col.groupable );
    }

    getMeasureColumnFields(): string[] {
        return this.getMeasureColumns()
            .map( col => col.field );
    }

    /**  Liefert Spalten über die Zeilen gruppiert werden können */
    getAttributeColumns(): Column[] {
        return this.getVisibleColumns().filter( col => col.groupable );
    }

    private getAttributeColumnFields(): string[] {
        return this.getAttributeColumns()
            .map( col => col.field );
    }

    onlyUnique( value: any, index: number, self: any ) {
        return self.indexOf( value ) === index;
    }

    getTotalRow(): Row {
        let totalRows = this.rows.filter( row => row.aggregated === -1 );
        let totalRow = totalRows[0];
        this.measureColumnIds = this.getMeasureColumnFields();
        for ( let otherTotalRow of totalRows ) {
            for ( let measure of this.measureColumnIds ) {
                if ( otherTotalRow.cells[measure] ) {
                    totalRow.cells[measure] = otherTotalRow.cells[measure];
                }
            }
        }
        return totalRow;
    }

    //alle Bi_Analysen haben eine info
    isBiAnalyse(): boolean {
        if ( this.info?.facttables.length ?? 0 > 0 ) {
            return true;
        }
        return false;
    }


    static findMax( rows: Row[] ): number {
        let max: number = 0;
        for ( let i = 1; i < rows.length; i++ ) {
            if ( rows[i].aggregated !== undefined ) {
                if ( rows[i].aggregated! > max ) {
                    max = rows[i].aggregated!;
                }
            }
        }
        return max;
    }
}
